import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';
/**
 * 在竖屏正方向下的安全区域
 */
export type SafeArea = {
  /**
   * 安全区域左上角横坐标
   * @example iphonex `44`
   */
  left: number;
  /**
   * 安全区域右下角横坐标
   * @example iphonex `375`
   */
  right: number;
  /**
   * 安全区域左上角纵坐标
   * @example iphonex `44`
   */
  top: number;
  /**
   * 安全区域右下角纵坐标
   * @example iphonex `778`
   */
  bottom: number;
  /**
   * 安全区域的宽度，单位逻辑像素
   * @example iphonex `375`
   */
  width: number;
  /**
   * 安全区域的高度，单位逻辑像素
   * @example iphonex `734`
   */
  height: number;
};

/**
 * 获取标准系统设备信息
 */
export type FabricGetSystemInfoCallbackData = {
  /**
   * 设备品牌
   */
  brand: string;
  /**
   * 设备型号。新机型刚推出一段时间会显示unknown，微信会尽快进行适配。
   *   case "iPod1,1":                               return "iPod Touch 1G"
   *   case "iPod2,1":                             return "iPod Touch 2G"
   *   case "iPod3,1":                             return "iPod Touch 3G"
   *   case "iPod4,1":                             return "iPod Touch 4G"
   *   case "iPod5,1":                             return "iPod Touch (5 Gen)"
   *   case "iPod7,1":                             return "iPod touch 6G"
   * ///iphone
   *   case "iPhone1,1":                           return "iPhone 1G"
   *   case "iPhone1,2":                           return "iPhone 3G"
   *   case "iPhone2,1":                           return "iPhone 3GS"
   *   case "iPhone3,1", "iPhone3,2", "iPhone3,3": return "iPhone 4"
   *   case "iPhone4,1":                           return "iPhone 4S"
   *   case "iPhone5,1", "iPhone5,2":              return "iPhone 5"
   *   case "iPhone5,3","iPhone5,4":               return "iPhone 5C"
   * @example `iPhone X`
   */
  model: string;
  /**
   * 设备像素比
   * @example `3`
   */
  pixelRatio: number;
  /**
   * 屏幕宽度，单位px
   * @example iphonex `375`
   */
  screenWidth: number;
  /**
   * 屏幕高度，单位px
   * @example '768'
   */
  screenHeight: number;
  /**
   * 屏幕真实高度，在刘海屏的时候不会减去刘海屏高度，单位px, android独有.
   * @example '804'
   */
  realScreenHeight?: number;
  /**
   * 可使用窗口宽度，单位px
   * @example iphonex `375`
   */
  windowWidth: number;
  /**
   * 可使用窗口高度，单位px
   * @example iphonex `642`
   */
  windowHeight: number;
  /**
   * 状态栏的高度，单位px
   * @example `44`
   */
  statusBarHeight: number;
  /**
   * 设置的语言, 默认`zh_CN`
   */
  language: string;
  /**
   * 客户端平台
   *  @example `devtools`
   */
  platform: string;
  /**
   * 地理位置定位能力是否可用, 如果定位被用户强制关闭, 或者在设置里面禁用了则为不可用, 如果能弹出让用户授权允许的弹窗认为是可用.
   * 此值参考值, 如果用户初次进入app打开了定位授权, 中途用户自行关闭了设置, 这个值不会得到及时更新, 如果需要及时准确请使用插件`get_location`
   */
  locationEnabled: boolean;
  /**
   * Wi-Fi 的系统开关
   */
  wifiEnabled: boolean;
  /**
   * 在竖屏正方向下的安全区域
   */
  safeArea: SafeArea;
  /**
   * IOS 专有, true 表示模糊定位，false 表示精确定位
   */
  locationReducedAccuracy: boolean;
  /**
   * 统当前主题，取值为light或dark，全局配置"darkmode":true时才能获取，否则为 undefined （不支持小游戏）
   */
  theme: 'dark' | 'light';
  /**
   * 是否已打开调试。可通过右上角菜单或 fabric.setEnableDebug 打开调试。
   */
  enableDebug: boolean;
  /**
   * 设备方向
   * @example `portrait`
   */
  deviceOrientation: 'portrait' | 'landscape';
  /**
   * 操作系统及版本
   * @example `iOS 10.0.1`
   */
  system: string;
  /**
   * 当前应用的名字.
   */
  appName: string;
  /**
   * App版本号, 当前应用的版本号.`1.0.0`
   */
  appVersion: string;

  /**
   * 纬度，范围为 -90~90，负数表示南纬, 获取缓存的版本
   * `getLocation`插件会更新此缓存
   */
  latitude: string;
  /**
   * 经度，范围为 -180~180，负数表示西经, 获取缓存的版本
   * `getLocation`插件会更新此缓存
   */
  longitude: string;
  /**
   * IOS 专有
   */
  idfa?: string;
  /**
   * Android 设备特有, IOS 无法获取
   */
  mac?: string;

  /**
   * 渠道, ios只有一个appStore
   */
  appChannel?: string;

  /**
   * Android 设备特有
   * 规则就行imei > oaid > android_id
   */
  androidId?: string;

  /**
   * Android 设备特有, 移动设备国际身份码 15位数字组成，有一定编码规则
   */
  imei?: string;

  /**
   * Android 设备特有
   */
  oaid?: string;

  /**
   * 网络类型
   * @example  'WIFI  | GPRS |  2G | 2.75G Edge | 3G | 3.5G HSDPA | 3.5G HSUPA | HRPD | 4G | 5G | 5G NSA'
   */
  networkType: string;

  /**
   * clientIP地址, android:1; iOS:1
   * @example '10.10.35.37'
   */
  clientIp: string;

  /**
   * 设备号, 每个设备生成的唯一值, 标识当前设备, 相对稳定
   * @example '63A20CC9-9978-42B7-9689-771F415A6B75'
   */
  deviceId: string;

  /**
   * refId, 渠道ID, 当前应用的下载渠道, android独有.
   * @example '5866741'
   */
  refId: string;
  /**
   * 设备标识符, ios独有.
   */
  adid?: string;
  /**
   * 设备指纹
   */
  deviceToken: string;
  /**
   * 设备名称
   * @example 'OPPO Reno9 5G'、'iPhone14,2'
   */
  deviceName: string;
};

/**
 * 获取系统信息。会使用同步实现来返回。
 */
export const getSystemInfo = () => {
  const result = core.call<
    'fabric_utils.getSystemInfo',
    FabricGetSystemInfoCallbackData
  >('fabric_utils.getSystemInfo');
  const fabricErrors = filterFabricErrors(
    result.code,
    result.message,
    result.data
  );
  if (fabricErrors) {
    throw fabricErrors;
  } else {
    return result.data;
  }
};
